import { useEffect, useRef } from 'react';

export default function useTargetAnnotationNavigation({
  targetAnnotationId,
  chapterAnnotations,
  onClearTargetAnnotation,
  isProgrammaticScrollRef,
  setCurrentPage,
  pageRefs,
  containerRef,
}) {
  const lastTargetAnnotationIdRef = useRef(null);

  useEffect(() => {
    if (!targetAnnotationId) {
      lastTargetAnnotationIdRef.current = null;
      return;
    }

    if (!chapterAnnotations || chapterAnnotations.length === 0) {
      console.log('⏳ Waiting for annotations to load before scrolling to:', targetAnnotationId);
      return;
    }

    if (lastTargetAnnotationIdRef.current === targetAnnotationId) return;
    lastTargetAnnotationIdRef.current = targetAnnotationId;

    console.log('📍 Scrolling to annotation:', targetAnnotationId);
    console.log('🔍 Available annotations:', chapterAnnotations?.length || 0);
    console.log('🔍 First annotation sample:', chapterAnnotations?.[0]);

    const annotation = chapterAnnotations.find(
      (a) => String(a.id) === String(targetAnnotationId) || String(a.annotation_id) === String(targetAnnotationId)
    );

    if (!annotation) {
      console.warn('⚠️ Annotation not found for scrolling:', targetAnnotationId);
      console.warn(
        '⚠️ Searched in annotations:',
        chapterAnnotations?.map((a) => ({ id: a.id, annotation_id: a.annotation_id, activity_id: a.activity_id }))
      );
      return;
    }

    console.log('✅ Found annotation:', annotation);

    const pageNum = parseInt(annotation.page_number, 10);
    if (!pageNum) {
      console.warn('⚠️ Annotation has no page number:', annotation);
      return;
    }

    console.log('📄 Scrolling to page:', pageNum);

    isProgrammaticScrollRef.current = true;
    setCurrentPage(pageNum);

    const attemptScroll = (attempts = 0) => {
      const pageContainer = pageRefs.current[pageNum];
      if (!pageContainer || !containerRef.current) {
        if (attempts < 20) {
          setTimeout(() => attemptScroll(attempts + 1), 100);
        } else {
          isProgrammaticScrollRef.current = false;
        }
        return;
      }

      const pageHeight = pageContainer.clientHeight || pageContainer.scrollHeight;
      const topPercent = parseFloat(annotation.position_top) || 0;
      const pixelOffset = (topPercent / 100) * pageHeight;

      pageContainer.scrollIntoView({ behavior: 'auto', block: 'start' });

      const container = containerRef.current.closest('.overflow-auto') || containerRef.current;

      if (container) {
        container.scrollTop += pixelOffset - 100;
      }

      setTimeout(() => {
        isProgrammaticScrollRef.current = false;
        console.log('✅ Programmatic scroll complete, re-enabling scroll updates');
      }, 500);

      if (onClearTargetAnnotation) {
        setTimeout(() => {
          onClearTargetAnnotation();
        }, 1200);
      }
    };

    requestAnimationFrame(() => {
      requestAnimationFrame(() => attemptScroll());
    });
  }, [
    targetAnnotationId,
    chapterAnnotations,
    onClearTargetAnnotation,
    isProgrammaticScrollRef,
    setCurrentPage,
    pageRefs,
    containerRef,
  ]);
}
